// Notifications inbox — every authenticated user sees their own.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { notifications } from '@/lib/api';
import type { NotificationKind, NotificationListItem } from '@frt/shared-types';

const KINDS: NotificationKind[] = [
  'upload_complete',
  'account_went_stale',
  'snapshot_locked',
  'manual_entry_due',
  'user_created',
  'user_role_changed',
];

type StateFilter = 'all' | 'unread' | 'read';

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<NotificationKind | ''>('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [page, setPage] = useState(1);

  const queryKey = ['notifications', kind, stateFilter, page] as const;
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      notifications.list({
        kind: kind === '' ? undefined : kind,
        state: stateFilter === 'all' ? undefined : stateFilter,
        page,
        per_page: 50,
      }),
  });

  const markRead = useMutation({
    mutationFn: (id: number) => notifications.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAll = useMutation({
    mutationFn: () => notifications.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Notifications</h1>
        <div className="flex items-center gap-3">
          {data && (
            <span className="text-sm text-slate-500">
              Unread: <strong>{data.unread_count}</strong>
            </span>
          )}
          <Button
            variant="secondary"
            disabled={!data || data.unread_count === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            Mark all read
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block text-sm">
              <span className="text-slate-600">Kind</span>
              <select
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as NotificationKind | '');
                  setPage(1);
                }}
                className="block rounded border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">all</option>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">State</span>
              <select
                value={stateFilter}
                onChange={(e) => {
                  setStateFilter(e.target.value as StateFilter);
                  setPage(1);
                }}
                className="block rounded border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="all">all</option>
                <option value="unread">unread</option>
                <option value="read">read</option>
              </select>
            </label>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inbox</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (data?.data ?? []).length === 0 ? (
            <div className="text-sm text-slate-500">No notifications.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data!.data.map((n) => (
                <NotificationRow
                  key={n.id}
                  note={n}
                  onMarkRead={() => markRead.mutate(n.id)}
                />
              ))}
            </ul>
          )}
          <Pagination
            page={data?.page ?? 1}
            perPage={data?.per_page ?? 50}
            total={data?.total ?? 0}
            onPage={setPage}
          />
        </CardBody>
      </Card>
    </div>
  );
}

function NotificationRow({
  note,
  onMarkRead,
}: {
  note: NotificationListItem;
  onMarkRead: () => void;
}) {
  const target = targetUrl(note);
  const message = messageFor(note);
  const unread = note.read_at === null;
  return (
    <li className="flex items-start justify-between gap-4 py-3">
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span
            className={
              unread
                ? 'inline-block h-2 w-2 rounded-full bg-brand-600'
                : 'inline-block h-2 w-2 rounded-full bg-slate-300'
            }
            aria-hidden="true"
          />
          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono">{note.kind}</span>
          <span>{new Date(note.created_at).toLocaleString()}</span>
          {note.read_at && (
            <span className="text-slate-400">read {new Date(note.read_at).toLocaleString()}</span>
          )}
        </div>
        <div className={unread ? 'text-slate-900' : 'text-slate-600'}>
          {target ? (
            <Link to={target as never} className="hover:underline" onClick={onMarkRead}>
              {message}
            </Link>
          ) : (
            <span>{message}</span>
          )}
        </div>
      </div>
      <div className="shrink-0">
        {unread && (
          <Button size="sm" variant="ghost" onClick={onMarkRead}>
            Mark read
          </Button>
        )}
      </div>
    </li>
  );
}

/**
 * Build a click-through target from the payload. Accepts an explicit
 * `target_url` if the producer included one; otherwise derive from the kind.
 */
function targetUrl(n: NotificationListItem): string | null {
  const p = n.payload;
  if (p && typeof p['target_url'] === 'string') return p['target_url'];
  switch (n.kind) {
    case 'upload_complete': {
      const id = p?.['upload_id'];
      return typeof id === 'number' ? `/tiller/history?highlight=${id}` : '/tiller/history';
    }
    case 'snapshot_locked': {
      const id = p?.['snapshot_id'];
      return typeof id === 'number' ? `/snapshots?highlight=${id}` : '/snapshots';
    }
    case 'account_went_stale': {
      const id = p?.['account_id'];
      return typeof id === 'number' ? `/accounts/${id}` : '/accounts';
    }
    case 'manual_entry_due': {
      const period = p?.['period'];
      return typeof period === 'string' ? `/manual-entry?period=${period}` : '/manual-entry';
    }
    case 'user_created':
    case 'user_role_changed':
      return '/profile';
    default:
      return null;
  }
}

function messageFor(n: NotificationListItem): string {
  const p = n.payload;
  const explicit = p && typeof p['message'] === 'string' ? p['message'] : null;
  if (explicit) return explicit;
  switch (n.kind) {
    case 'upload_complete':
      return 'A Tiller upload has completed.';
    case 'snapshot_locked':
      return 'A monthly snapshot was locked.';
    case 'account_went_stale':
      return 'An account stopped reporting fresh balances.';
    case 'manual_entry_due':
      return 'Manual balance entry is due for the current period.';
    case 'user_created':
      return 'Your account was created.';
    case 'user_role_changed':
      return 'Your role was changed.';
    default:
      return n.kind;
  }
}

function Pagination({
  page,
  perPage,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (p: number) => void;
}) {
  if (total === 0) return null;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  return (
    <div className="flex items-center justify-between text-xs text-slate-500">
      <div>{`Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} of ${total}`}</div>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          ← Prev
        </Button>
        <span className="self-center">
          Page {page} / {lastPage}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
        >
          Next →
        </Button>
      </div>
    </div>
  );
}
